import { getImageSrcUrl, isProfessional, mapFormatDescription, mapDurationFromExtensionAttributes, mapEditionOrRelease } from '../../common/utils/product-util';
import { getFreeTrialDuration } from "../free-trial/utils/free-trial.util";
import { PaymentOrderDetails } from "./order-completion.interface";
import { mapCreateCoverImages, mapCreateTermsToDuration } from '../create-custom/mappings/create-custom.mapping';
import { parse } from '../../common/utils/util';
import { sanitizeInstructors } from '../create-custom/utils/create-custom-product.util';
import { mapMetaOfferContents } from '../cart/shop-cart/mapping/meta-offer.mapping';
import { mapSharpenUpsellPromo } from '../upsell/mapping/sharpen-upsell-promo.mapping';

export const mapOrderDetailsMagentoResponse = (orderResponse: any): PaymentOrderDetails => {
  let merchOriginalPriceTotal: number = orderResponse.items.reduce((acc: number, item: any) => {
    return acc + (item.original_price * item.qty_ordered);
  }, 0)

  let merchPriceTotal: number = orderResponse.items.reduce((acc: number, item: any) => {
    return acc + (item.price * item.qty_ordered);
  }, 0);

  let offerDiscountTotal: number = merchOriginalPriceTotal - merchPriceTotal;

  return {
    id: orderResponse.increment_id,
    currencyCode: orderResponse.base_currency_code,
    totalAmount: orderResponse.grand_total,
    totalFreight: orderResponse.base_shipping_amount,
    totalHandling: '0',
    totalTax: orderResponse.base_tax_amount,
    storeCode: orderResponse.store_name,
    status: orderResponse.status,
    metaOfferId: orderResponse.extension_attributes?.meta_offer_id,
    orderedDate: orderResponse.created_at,
    paymentGateway: orderResponse?.payment?.method,
    purchaseOrderNumber: orderResponse?.payment?.po_number,
    digitalHandlingFees: orderResponse.extension_attributes?.digital_handling_fees || 0,
    orderItems: orderResponse.items.map((item) => {
      const extensionAttributes = item?.extension_attributes;
      const isProfessionalProduct = isProfessional(extensionAttributes?.business_unit ?? '');

      return {
        id: item.item_id,
        orderId: item.order_id,
        isbn10: extensionAttributes?.isbn10,
        isbn13: extensionAttributes?.isbn13,
        mhid: extensionAttributes?.mhid,
        title: item.name,
        quantity: item.qty_ordered,
        originalPrice: item.original_price,
        unitPrice: item.price,
        offerDiscount: parseFloat((item.original_price - item.price).toFixed(2)), // Fix mantissa issue visualization
        countryCode: extensionAttributes?.country_code,
        currencyCode: extensionAttributes?.currency_code,
        contentType: item.product_type,
        businessUnit: extensionAttributes?.business_unit,
        imageUrl: getImageSrcUrl(extensionAttributes?.isbn13 || extensionAttributes?.isbn),
        virtual: item.is_virtual,
        branding: extensionAttributes.offer_branding,
        displayCode: item?.extension_attributes?.display_code,
        accessCode: item?.extension_attributes?.access_code,
        instructions: item?.extension_attributes?.instructions,
        productMetaData: {
          grade: extensionAttributes?.grade,
          publishDate: extensionAttributes?.publish_date,
          copyrightYear: extensionAttributes?.copyright,
          businessUnit: extensionAttributes?.business_unit,
          contributors: extensionAttributes?.author_data.map((author: any) => ({
            contributorDisplayName: author.display_name,
          })),
          application: extensionAttributes.application,
          editionNo: extensionAttributes?.edition,
          editionOrRelease: mapEditionOrRelease(extensionAttributes),
          looseLeafAvailable: extensionAttributes?.loose_leaf_available,
          hasEbook: extensionAttributes?.is_ebook,
          rentalAvailable: extensionAttributes?.rental_available,
          isConnectSharpenBundle: extensionAttributes?.is_sharpen,
          isSharpenCompanion: extensionAttributes?.is_sharpen_companion,
          isSharpenAdvantage: extensionAttributes?.is_sharpen_advantage,
          ...mapDurationFromExtensionAttributes(extensionAttributes),
          launchObject: extensionAttributes?.launch_object,
          cancelUrl: extensionAttributes?.cancel_url,
          exitUrl: extensionAttributes?.exit_url,
          programSourceId: extensionAttributes?.program_source_id ? extensionAttributes?.program_source_id[0] : '',
          programSourceName: extensionAttributes?.program_source_name,
          offerType: extensionAttributes.offer_type,
          isEvergreen: item?.extension_attributes?.is_evergreen,
          itemType: item?.extension_attributes?.item_type,
          flow: item?.extension_attributes?.flow,
          format: {
            formatDescription: mapFormatDescription(extensionAttributes),
            formatTypeDescription: extensionAttributes?.format_type_desc,
          },
          offerPricingId: extensionAttributes?.product_offer_id,
          sku: item.sku,
          boardsBeyondUrl: extensionAttributes?.boardsbeyond_url,
          ...mapCreateTermsToDuration(extensionAttributes?.terms),
          metaOfferContentsMetadata: mapMetaOfferContents(extensionAttributes?.meta_offer_contents),
          ...(isProfessionalProduct && extensionAttributes.title_type_code !== undefined && { titleType: extensionAttributes.title_type_code }),
          ...(isProfessionalProduct && extensionAttributes.division !== undefined && { division: extensionAttributes.division }),
        },
        createCustomProductMetadata: {
          courses: parse(extensionAttributes?.course_info) || [],
          createCoverImages: mapCreateCoverImages(extensionAttributes?.cover_images),
          deliveryTypes: parse(extensionAttributes?.delivery_type) || [],
          department: extensionAttributes?.department,
          instructorNames: sanitizeInstructors(parse(extensionAttributes?.instructor_name) || []),
          school: parse(extensionAttributes?.school),
          supplements: parse(extensionAttributes?.supplements) || [],
        },
        freeTrialInfo: {
          isFreeTrial: extensionAttributes?.free_trial,
          startDate: extensionAttributes?.trial_start_date,
          endDate: extensionAttributes?.trial_end_date,
          freeTrialDuration: extensionAttributes?.trial_end_date ? getFreeTrialDuration(extensionAttributes.trial_start_date, extensionAttributes.trial_end_date) : '0',
          subscriptionAmount: extensionAttributes?.subscription_amount,
          subscriptionUnitPrice: extensionAttributes?.subscription_unit_price,
          subscriptionTax: extensionAttributes?.subscription_tax,
          subscriptionDigitalServiceFees: extensionAttributes?.subscription_digital_service_fees,
          purchaseOrderNumber: extensionAttributes?.purchase_order_number
        },
        eBookUrls: extensionAttributes?.ebooks_urls,
        durationType: extensionAttributes?.base_product ? 'standard' : 'alternate',
        ...(extensionAttributes?.application && { application: extensionAttributes?.application }),
      };
    }),
    tokenOrderMetaData: {
      ...orderResponse?.tokenOrderMetaData,
      requestIdentifier: orderResponse.extension_attributes.business_unit
    },
    promocode: orderResponse?.coupon_code,
    totalDiscount: orderResponse?.discount_amount ? Math.abs(orderResponse?.discount_amount).toString() : '0',
    sharpenPdpUrl: orderResponse?.extension_attributes?.sharpen_url,
    sharpenUpsellPromo: mapSharpenUpsellPromo(orderResponse?.extension_attributes?.sharpen_promo),
    merchPriceTotal: parseFloat(merchPriceTotal.toFixed(2)),
    offerDiscountTotal: parseFloat(offerDiscountTotal.toFixed(2)),
    merchOriginalPriceTotal: parseFloat(merchOriginalPriceTotal.toFixed(2)),
    isSampleOrder: orderResponse.extension_attributes?.is_sample_order,
    accountAge: orderResponse.extension_attributes?.customer_email_age_days,
  };
};
